/* eslint-disable max-lines-per-function */
import {
  Button,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from '@e2n/ui';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useState } from 'react';

interface YearMonthPickerDrawerProps {
  year: number;
  month: number;
  onSelect: (year: number, month: number) => void;
  confirmLabel: string;
  triggerClassName?: string;
  children: React.ReactNode;
}

export function YearMonthPickerDrawer({
  year,
  month,
  onSelect,
  confirmLabel,
  triggerClassName,
  children,
}: YearMonthPickerDrawerProps) {
  const [displayedYear, setDisplayedYear] = useState(year);

  const handlePrevYear = () => setDisplayedYear((y) => y - 1);
  const handleNextYear = () => setDisplayedYear((y) => y + 1);

  const handleMonthSelect = (monthIndex: number) => {
    onSelect(displayedYear, monthIndex + 1);
  };

  const monthNames = Array.from({ length: 12 }, (_, idx) =>
    dayjs().locale('de').month(idx).format('MMM').slice(0, 3),
  );

  return (
    <Drawer>
      <DrawerTrigger asChild className={triggerClassName}>
        {children}
      </DrawerTrigger>
      <DrawerContent className="flex flex-col">
        <DrawerTitle className="sr-only">{confirmLabel}</DrawerTitle>
        <DrawerDescription className="sr-only">
          {confirmLabel}
        </DrawerDescription>
        <div
          className="w-full p-4 overflow-y-auto flex-1 min-h-0"
          data-vaul-no-drag>
          <div className="flex flex-col items-center gap-3">
            <div className="flex w-full items-center justify-between">
              <Button
                onClick={handlePrevYear}
                variant="ghost-primary"
                size="icon">
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>
              <div className="text-sm font-semibold">{displayedYear}</div>
              <Button
                onClick={handleNextYear}
                size="icon"
                variant="ghost-primary">
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2 w-full">
              {monthNames.map((monthName, idx) => {
                const isSelected = displayedYear === year && month === idx + 1;
                return (
                  <div key={idx} className="flex justify-center">
                    <Button
                      onClick={() => handleMonthSelect(idx)}
                      className="font-normal w-9/12"
                      size="sm"
                      variant={isSelected ? 'default' : 'ghost'}>
                      {monthName}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-neutrals-200 shrink-0">
          <DrawerClose asChild>
            <Button className="w-full" size="lg">
              {confirmLabel}
            </Button>
          </DrawerClose>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
